import React, { useState, useCallback, useEffect } from "react";
import Input from "src/components/Input";
import { SearchIcon } from "src/assets/icons/search";
import type { Attachment, DigiFiCustomer } from "src/types/conditionsInbox";
import type { ApiApplicationResponse } from "src/types/api";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { useDebounce } from "src/hooks/useDebounce";
import { CustomerResultsSection } from "./CustomerResultsSection";
import {
  SearchSectionWrapper,
  SearchForm,
  SearchInputWrapper,
  SearchIconWrapper,
  PlaceholderText,
} from "./SearchSection.styles";

interface SearchSectionProps {
  selectedEmailId: string | null;
  attachments: Attachment[];
  renamedAttachments?: Record<string, string>;
  sendFiles: (
    emailId: string,
    attachmentsId: Set<string>,
    customer: DigiFiCustomer,
  ) => Promise<void>;
}

export const SearchSection: React.FC<SearchSectionProps> = ({
  selectedEmailId,
  attachments,
  renamedAttachments,
  sendFiles,
}) => {
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<DigiFiCustomer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [isSendingFiles, setIsSendingFiles] = useState(false);
  const [sentToCustomers, setSentToCustomers] = useState<Set<string>>(
    new Set(),
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 750);

  const performSearch = useCallback((term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    safeApiCall(() =>
      fetchFromApi("/admin/v1/borrower-applications", {
        method: "GET",
        queryParams: { search: term.trim() },
      }),
    )
      .then((response) => {
        if (response && Array.isArray(response)) {
          const customers: DigiFiCustomer[] = response.map(
            (app: ApiApplicationResponse) => ({
              id: app.application_id,
              name: app.borrower.full_name,
              phone: app.borrower.phone_number,
              email: app.borrower.email,
              address: app.borrower.address,
              coBorrower: app.co_borrower?.full_name,
              intermediary: app.intermediary?.name,
              applicationStatus: app.application_status,
            }),
          );

          setSearchResults(customers);
        } else {
          setSearchResults([]);
        }
      })
      .catch((error) => {
        console.error("Search failed:", error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  }, []);

  useEffect(() => {
    performSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, performSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchTerm);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleCard = (customer: DigiFiCustomer) => {
    if (expandedCard === customer.id) {
      setExpandedCard(null);
    } else {
      setExpandedCard(customer.id);
    }
  };

  const handleSendFiles = (
    customer: DigiFiCustomer,
    attachmentsId: Set<string>,
  ) => {
    if (!selectedEmailId) return;

    setIsSendingFiles(true);
    sendFiles(selectedEmailId, attachmentsId, customer)
      .then(() => {
        setSentToCustomers((prev) => new Set([...prev, customer.id]));
      })
      .catch((error) => {
        console.error("Failed to send files:", error);
      })
      .finally(() => {
        setIsSendingFiles(false);
      });
  };

  return (
    <SearchSectionWrapper>
      <SearchForm onSubmit={handleSearch}>
        <SearchInputWrapper>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Input
            id="digifi-search"
            name="search"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => {}}
            onBlur={() => {}}
            placeholder="Search for customers in DigiFi"
            showError={false}
            ariaLabel="Search for customers in DigiFi"
          />
        </SearchInputWrapper>
      </SearchForm>

      {isSearching && <PlaceholderText>Searching...</PlaceholderText>}

      <CustomerResultsSection
        attachments={attachments}
        renamedAttachments={renamedAttachments}
        searchResults={searchResults}
        expandedCard={expandedCard}
        sentToCustomers={sentToCustomers}
        isSendingFiles={isSendingFiles}
        onToggleCard={handleToggleCard}
        onSendFiles={handleSendFiles}
        selectedEmailId={selectedEmailId}
        searchTerm={searchTerm}
      />
    </SearchSectionWrapper>
  );
};
